import { retrieveDiscussionGroupsV2 } from '@goldcast/api/backend';
import debounce from 'lodash.debounce';
import { GenericMessageDialog, GenericHeaderNotification } from 'uicomponents';
import { ActionContext, Module } from 'vuex';

import { PropType } from './avroom/mutations';
import { Broadcast } from './event/types';
import { mappingToSocketProcessorHandler, processSocketMessage, SocketEventType } from './socketMessageProcessor';

import analytics from '@/analytics';
import { getEnv } from '@/constants';
import { mattermostApiClient } from '@/services/apiClient';
import DialogService from '@/services/dialogs/DialogService';
import { loadDiscussionGroups } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { assignMattermostUserToTeam } from '@/services/mattermostUserAssignment.service';
import { MMClient } from '@/services/socketClient/mmClient';
import { migratedPostRecieved } from '@/services/textQNA.service';
import trackerService from '@/services/tracker.service';
import { UserRole } from '@/services/user.service';
import store, { RootState } from '@/store';
import { scheduleReactionChange, scheduleRefresh, scheduleUserAdd } from '@/store/schedulers';
import { getCurrentRoute } from '@/utils/routerAccess';

let socket: MMClient | null = null;
let publicChannelsInFlight: Promise<any> | null = null;

const channelRefreshDebouncedFunctions: Record<string, any> = {};
// caps getJoinedChannels' post-assignment retry to once per team, avoiding unbounded 403/404 -> assign -> retry loops
const teamsRetriedAfterAssignment = new Set<string>();
export enum SERVER_CLIENT_COMMUNICATION_MODE {
  WEB_SOCKETS = 'websockets',
  SSE = 'server-sent-events'
}
export type MattermostState = {
  timeout: number | null;
  persist: boolean;
  joinedChannels: {
    state: 'notstarted' | 'loading' | 'loaded' | 'error';
    value: Record<
      string,
      {
        name: string;
        id: string;
        type: 'D' | 'O';
      }
    >;
    loadingpromise?: Promise<any>;
  };
  dgChannels: Record<string, string[]>;
  discussionGroups: Record<
    string,
    {
      title: string;
      capacity: number;
      moderators: {
        items: any[];
      };
      status: any;
      tags: string[];
      sponsorImage: string;
      mmid: string;
    }
  >;
  sortedDiscussionGroupIDs: string[];
  mattermostRetries: number;
  publicChannels: {
    townhall: string | null;
    offtopic: string | null;
  };
  firstConnectDone: boolean;
  mmEverConnected: boolean;
  userStatuses: Record<string, 'online' | 'offline' | 'away'>;
  groupsLoading: boolean;
  socketstatus: 'offline' | 'online' | 'error' | 'bailed' | 'bailed-noretry' | 'closed-forver';
  discussionGroupStatuses: Record<string, 'loading' | 'loaded' | 'error'>;
  discussionGroupLoadingInBackground: Promise<any> | null;
  channelLeavePromise: Promise<any> | null;
  serverClientCommunicationMode: SERVER_CLIENT_COMMUNICATION_MODE;
};

type GettersArgType = {
  [k in keyof typeof chatgetters]: ReturnType<(typeof chatgetters)[k]>;
};

const chatgetters = {
  otherUser: (state: MattermostState, g: any, rootState: RootState, rootGetters: any) => (mmids: string[]) => {
    const a = rootState.event.userStateInvalidated;
    const getters = g as GettersArgType;
    const currentUser = rootGetters.getCurrentUser;
    const othermmid = mmids.find(mmid => mmid !== currentUser?.mmid);
    const allusers = rootState.event.event?.users || {};
    if (othermmid) {
      const otherUser = allusers[othermmid];
      if (otherUser && otherUser.eventUser && !otherUser.eventUser.blocked) {
        return otherUser;
      }
      return null;
    }
    return null;
  },
  groups(state: MattermostState, g: any, rS: any, rootGetters: any) {
    const getters = g as GettersArgType;
    const dgChannelKeys = state.sortedDiscussionGroupIDs;
    // This number is assuming that channel capacity should be such that there can be max 6 attendees in a row.
    // I can't think of a more generic logic for this as the UI is designed to be like this

    const groups = dgChannelKeys.map(key => {
      const channel = state.discussionGroups[key] || {};
      const channelMMids = state.dgChannels[key] || [];
      const channelUsers = channelMMids.map(mmid => {
        const user = rootGetters['event/usersbyMmid'][mmid];
        if (!user) {
          scheduleRefresh(mmid);
        }
        return user;
      });
      const channelModeratorsSet = new Set(
        (channel.moderators && channel.moderators.items.map(item => item.userID)) || []
      );

      const participants = channelUsers.filter(user => user && !channelModeratorsSet.has(user.id));

      const moderators = channelUsers.filter(user => user && channelModeratorsSet.has(user.id));

      const openspots = channel.capacity - moderators.length - participants.length;

      const group = {
        id: key,
        status: channel.status,
        title: channel.title,
        tags: channel.tags || [],
        capacity: channel.capacity,
        count: participants.length + moderators.length,
        openspots: openspots >= 0 ? openspots : 0,
        sponsorlogo: channel.sponsorImage,
        moderators: moderators.slice(0, 3).map(moderator => ({
          ...moderator,
          email: moderator.display_email
        })),
        attendees: participants.slice(0, 12).map(participant => ({
          ...participant,
          email: participant.display_email
        }))
      };
      return group;
    });
    return groups;
  },
  dynamicDiscussionGroups(state: MattermostState, g: any, rootState: RootState, rootGetters: any) {
    const getters = g as GettersArgType;
    // TODO: type the event module and remove this any
    return (rootState.event.discussionGroups as any[]).map(channel => {
      const dgChannel = state.dgChannels[channel.mmid] || [];
      const channelUsers = dgChannel
        .map(mmid => {
          const user = rootGetters['event/usersbyMmid'][mmid];
          if (!user) {
            scheduleRefresh(mmid);
          }
          return user;
        })
        .filter(user => {
          if (user && user.mmid) {
            return state.userStatuses[user.mmid] === 'away' || state.userStatuses[user.mmid] === 'online';
          }
          return false;
        });
      const organisers = channelUsers.filter(
        user => user && (user.event_role == UserRole.ADMIN || user.event_role == 1 || user.event_role == UserRole.STAFF)
      );
      const participants = channelUsers.filter(
        user =>
          user && !(user.event_role == UserRole.ADMIN || user.event_role == 1 || user.event_role == UserRole.STAFF)
      );
      const totalParticipants = [...participants, ...organisers];

      const canOrganiserJoin = rootGetters['isEventOrganizer'] && totalParticipants.length < 32;

      const group = {
        id: channel.id,
        status: state.discussionGroupStatuses[channel.mmid],
        title: channel.title,
        tags: channel.tags || [],
        capacity: channel.capacity,
        count: participants.length,
        openspots: (function () {
          const remainingSeats = channel.capacity - participants.length;
          if (totalParticipants.length == 32 || remainingSeats < 0) {
            return 0;
          }
          return remainingSeats;
        })(),
        sponsorlogo: channel.sponsor_image,
        canOrganiserJoin: canOrganiserJoin,
        attendees: totalParticipants.map(participant => ({
          ...participant,
          email: participant.display_email
        }))
      };
      return group;
    });
  },
  boothWithGroups(state: MattermostState, getters: any, rootState: RootState, rootGetters: any) {
    const booth = rootGetters['event/currentEventBooth'];
    if (booth) {
      return {
        ...booth,
        id: booth.id,
        title: booth.name,
        discussionGroups: (booth.discussion_group_objects as any[]).map((it: any) => {
          const dgChannel = state.dgChannels[it.mmid] || [];
          const channelUsers = dgChannel
            .map((mmid: any) => {
              const user = rootGetters['event/usersbyMmid'][mmid];
              if (!user) {
                scheduleRefresh(mmid);
              }
              return user;
            })
            .filter(user => {
              if (user && user.mmid) {
                return state.userStatuses[user.mmid] === 'away' || state.userStatuses[user.mmid] === 'online';
              }
              return false;
            });
          const organisers = channelUsers.filter(
            user =>
              user && (user.event_role == UserRole.ADMIN || user.event_role == 1 || user.event_role == UserRole.STAFF)
          );
          const participants = channelUsers.filter(
            user =>
              user && !(user.event_role == UserRole.ADMIN || user.event_role == 1 || user.event_role == UserRole.STAFF)
          );
          const totalParticipants = [...participants, ...organisers];

          const canOrganiserJoin = rootGetters['isEventOrganizer'] && totalParticipants.length < 32;

          const group = {
            ...it,
            id: it.id,
            status: state.discussionGroupStatuses[it.mmid],
            title: it.title,
            tags: it.tags || [],
            capacity: it.capacity,
            count: participants.length,
            openspots: (function () {
              const remainingSeats = it.capacity - participants.length;
              if (totalParticipants.length == 32 || remainingSeats < 0) {
                return 0;
              }
              return remainingSeats;
            })(),
            sponsorlogo: it.sponsor_image,
            canOrganiserJoin: canOrganiserJoin,
            attendees: totalParticipants.map(participant => ({
              ...participant,
              email: participant?.display_email
            }))
          };
          return group;
        }),
        logo: booth.logo,
        sponsorLink: booth.sponsor_link,
        heroasset: booth.hero_asset,
        heroasset_type: booth.hero_asset_type || 'image',
        description: booth.description,
        moderators: [],
        resources: booth.resources || [],
        cta: booth.cta,
        tags: booth.tags || [],
        mmid: booth?.mmid || ''
      };
    }
    return null;
  }
};

const mattermostmodule: Module<MattermostState, RootState> = {
  namespaced: true,
  state: {
    channelLeavePromise: null,
    timeout: null,
    persist: false,
    dgChannels: {},
    joinedChannels: {
      state: 'notstarted',
      value: {}
    },
    discussionGroups: {},
    sortedDiscussionGroupIDs: [],
    mattermostRetries: 0,
    publicChannels: {
      townhall: null,
      offtopic: null
    },
    firstConnectDone: false,
    mmEverConnected: false,
    userStatuses: {},
    groupsLoading: true,
    socketstatus: 'offline',
    discussionGroupStatuses: {},
    discussionGroupLoadingInBackground: null,
    serverClientCommunicationMode: SERVER_CLIENT_COMMUNICATION_MODE.WEB_SOCKETS
  },
  getters: chatgetters,
  mutations: {
    updateMmEverConnected(state, value) {
      state.mmEverConnected = value;
    },
    setServerClientCommunicationMode(state, mode: SERVER_CLIENT_COMMUNICATION_MODE) {
      state.serverClientCommunicationMode = mode;
    },
    setSocketStatus(state, status) {
      state.socketstatus = status;
    },
    setGroupsLoading(state, value) {
      state.groupsLoading = value;
    },
    setPublicChannelIds(state, channels) {
      state.publicChannels = {
        townhall: channels.townhall || null,
        offtopic: channels.offtopic || null
      };
    },
    setDgChannels(state, dgChannels) {
      state.dgChannels = dgChannels;
    },
    setChannel(state, { id, channel }) {
      state.dgChannels = { ...state.dgChannels, [id]: channel };
    },
    setDiscussionGroups(state, { groups, sortedIds }) {
      state.discussionGroups = groups;
      state.sortedDiscussionGroupIDs = sortedIds;
    },
    setDiscussionGroupStatus(state, { id, status }) {
      state.discussionGroupStatuses = {
        ...state.discussionGroupStatuses,
        [id]: status
      };
    },
    setUserStatus(state, { userID, status }) {
      if (state.userStatuses[userID] !== status) {
        state.userStatuses = { ...state.userStatuses, [userID]: status };
      }
    },
    setUserStatuses(state, statuses) {
      state.userStatuses = { ...state.userStatuses, ...statuses };
    }
  },
  actions: {
    makeMattermostCall(context, { url, method, body, configs, options = {} }) {
      function normalizeMethod(method: string): 'get' | 'put' | 'patch' | 'post' | 'delete' {
        return method.toLowerCase() as 'get' | 'put' | 'patch' | 'post' | 'delete';
      }

      if (!mattermostApiClient.authToken) {
        loggerService.warn('Mattermost API call failed:', {
          url,
          method,
          error: 'Unauthorized'
        });
        return;
      }

      return mattermostApiClient[normalizeMethod(method)](url, body, options, configs)
        .then(response => {
          return response.data;
        })
        .catch(error => {
          loggerService.error('Mattermost API call failed:', {
            url,
            method,
            error
          });
          throw error;
        });
    },
    disconnect(context) {
      context.state.firstConnectDone = false;
      if (socket) {
        socket.openPromise = null;
        socket.disconnect();
        // this is important, otherwise if user keeps switching between events,
        // mm will get connected with same team id for all the events
        socket = null;
      }
    },
    close(context) {
      if (!socket) {
        loggerService.warn('mmsocket: socket is null, could not close mm socket..');
        return;
      }
      socket.close();
      socket = null;
    },
    connect(context) {
      if (context.state.firstConnectDone || socket?.openPromise) {
        return;
      }
      if (!socket) {
        loggerService.info('mmsocket: creating MM instance..');
        createMMInstance();
      }
      if (socket) {
        trackerService.track('mattermost', 'connectionInitialized', { timestamp: Date.now() });
        socket.connect();
      } else {
        analytics.track('mm-null-socket', {
          currentRouteID: context.rootState.route.params.id
        });
        loggerService.error('mmsocket: socket is null, could not connect to mm socket..');
      }
    },

    sendupdateRoomMsgForWebsocket(context, roomId: string) {
      if (socket && roomId) {
        loggerService.info('mmsocket: sending update_room event', roomId);
        socket.sendMessage({
          seq: socket.counter++,
          action: 'update_room',
          data: {
            room_id: roomId
          }
        });
      }
    },

    processEvent(context, { msg, source }) {
      try {
        msg = JSON.parse(msg);
      } catch (e) {
        loggerService.error('Failed to parse socket message: ', msg);
        msg = 'Unparsable string: ' + msg;
      }

      try {
        const eventType: SocketEventType = msg.event || '';
        if (mappingToSocketProcessorHandler[eventType]) {
          processSocketMessage(msg.data, msg.event, source, !context.rootState.event.event.use_mm_sockets);
          return;
        }
        switch (msg.event) {
          case 'user_added_team':
            {
              if (msg && msg.data && typeof msg.data.channel_id === 'string') {
                const debouncedRefresh = makeRefreshFunction(context, msg.data.channel_id);
                debouncedRefresh();
              }
            }
            break;
          case 'user_removed_team':
            {
              if (msg && msg.data && typeof msg.data.channel_id === 'string') {
                const debouncedRefresh = makeRefreshFunction(context, msg.data.channel_id);
                debouncedRefresh();
              }
            }
            break;
          case 'user_removed':
            {
              context.dispatch('addUserBackIfNeeded', msg);
            }
            break;
          case 'added_to_team_broadcast':
            {
              const eventid = context.rootState.route.params.eventID.toString();
              scheduleUserAdd(eventid, msg.data.user_id);
            }
            break;
          case 'ephemeral_message':
            {
              if (!msg?.data?.post) {
                return;
              }
              const post = JSON.parse(msg.data.post);
              if (post?.message) {
                if (post.channel_id == context.rootState.event.avroom?.polls?.id) {
                  context.dispatch('handleAsyncPollWebsocketMessage', post);
                  return;
                }
                const isDiffCaptionsLanguageSelected =
                  context?.rootGetters['event/stage/isDiffCaptionsLanguageSelected'];
                const formattedMessage = JSON.parse(post.message);

                // prevent non tranlsated messages in translation mode
                if (isDiffCaptionsLanguageSelected && !formattedMessage.isTranslation) {
                  loggerService.log(`Prevented non translated message in translate mode ${post.channel_id}`, post);
                  return false;
                }

                const isWrongMessageForLiteMode = isLiteModeActive(context)
                  ? !formattedMessage.lite_mode
                  : formattedMessage.lite_mode;
                if (isWrongMessageForLiteMode) {
                  /*
                loggerService.log(
                  `Wrong message lite mode ${post.channel_id}`,
                  post
                );
                */
                  return false;
                }

                // check if captions are empty
                const isValidCaptions =
                  formattedMessage?.caption &&
                  Object.getPrototypeOf(formattedMessage?.caption) === Object.prototype &&
                  Object.keys(formattedMessage?.caption).length !== 0;
                if (!isValidCaptions) {
                  loggerService.info(`Invalid captions ${post.channel_id}`, post);
                  return false;
                }

                const currentRouteID = context.rootState.route.params.id;
                const broadcastDetails: Broadcast = context.rootGetters['event/currentEventBroadcast'](currentRouteID);
                const isLiveCaptionsChannel = broadcastDetails?.live_captions_mmid === post.channel_id;

                if (isLiveCaptionsChannel) {
                  context.dispatch('event/avroom/updateCaptions', post, {
                    root: true
                  });
                }
              }
            }
            break;
          case 'posted':
            {
              const post = JSON.parse(msg.data.post);

              if (post.message) {
                const message = post.message;

                if (post.props.alertMessage) {
                  if (getCurrentRoute().params.id === post.props.broadcast) {
                    if (context.rootGetters['event/isBroadcastSpeakerOrOrganizer']) {
                      DialogService.show(
                        GenericHeaderNotification,
                        {
                          text: message
                        },
                        {
                          hideOverlay: true,
                          cancelOnClickOutside: false,
                          blockBackgroundActions: false,
                          closeOnCTAClick: false,
                          autoCancelTimeout: 7000
                        }
                      );
                    }
                  }
                }

                if (post.props.kind === 'mute-user') {
                  const isRequestForCurrentUser = post.props.userId === store.state.user.currentEventUser?.id;
                  const isRequestForCurrentChannel = post.props.channelId === getCurrentRoute().params.id;
                  if (isRequestForCurrentUser && isRequestForCurrentChannel) {
                    const isUserASpeakerForChannel =
                      store.state.mediaroom.mediaRooms[post.props.channelId]?.userRole === 'host';
                    const isUserSpeaking = store.state.mediaroom.stageControls.audio;
                    if (isUserASpeakerForChannel && isUserSpeaking) {
                      store.commit('mediaroom/setStageControl', {
                        audio: false
                      });
                      store.dispatch('mediaroom/unpublishLocalTracks', {
                        channelName: post.props.channelId,
                        mediaTrackType: 'audio'
                      });
                      DialogService.show(
                        GenericHeaderNotification,
                        {
                          text: 'You have been muted by the host'
                        },
                        {
                          hideOverlay: true,
                          cancelOnClickOutside: false,
                          blockBackgroundActions: false,
                          closeOnCTAClick: false,
                          autoCancelTimeout: 7000
                        }
                      );
                    }
                  }
                }

                if (post.props.kind === 'pop-up') {
                  DialogService.show(GenericMessageDialog, {
                    title: post.props.title || '',
                    resolveLabel: post.props.cta || 'Okay',
                    message: message
                  });
                  context.dispatch(
                    'event/avroom/newPostReceived',
                    { post, data: msg.data },
                    {
                      root: true
                    }
                  );
                } else if (post.props.kind === 'P2PVideoCallPost') {
                  context.dispatch(
                    'event/avroom/receiveIncomingVideoCallMessage',
                    {
                      roomId: post.props.p2pVideoCallRoomId,
                      channel_id: post.channel_id,
                      whoIsVideoCallingId: post.props.whoIsVideoCallingId
                    },
                    { root: true }
                  );
                  context.dispatch(
                    'event/avroom/newPostReceived',
                    { post, data: msg.data },
                    {
                      root: true
                    }
                  );
                } else {
                  context.dispatch(
                    'event/avroom/newPostReceived',
                    { post, data: msg.data },
                    {
                      root: true
                    }
                  );
                }
              } else {
                context.dispatch(
                  'event/avroom/newPostReceived',
                  { post, data: msg.data },
                  {
                    root: true
                  }
                );
              }
            }
            break;
          case 'reaction_added':
            {
              scheduleReactionChange('add', msg);
            }
            break;
          case 'reaction_removed':
            {
              scheduleReactionChange('remove', msg);
            }
            break;
          case 'post_edited':
            {
              context.dispatch('event/avroom/processMsgEdit', msg, {
                root: true
              });
            }
            break;

          case 'post_migrated':
            migratedPostRecieved(msg);
            break;
          case 'post_deleted':
            {
              context.dispatch('event/avroom/deleteMessage', msg, {
                root: true
              });
            }
            break;
          // case "direct_added":
          //   {
          //     const teammate_id = msg.data.teammate_id;
          //     context.dispatch("event/avroom/openP2PChat", teammate_id);
          //   }
        }
      } catch (err) {
        loggerService.error('Failed to process event', err);
      }
    },
    getDebouncedChannelRefresher(context, channel_id) {
      const debouncedRefresh = makeRefreshFunction(context, channel_id);
      return debouncedRefresh;
    },
    handleAsyncPollWebsocketMessage(context, post) {
      const pollData = JSON.parse(post.message);
      context.dispatch('event/avroom/incrementAsyncPollVotes', pollData, {
        root: true
      });
    },
    addUserBackIfNeeded(context, msg) {
      // Delay 4 seconds just in case this is happening
      // because of a route navigation. Then allow the target route to be reached
      const ownmmid = context.rootState.user.currentUser?.mmid;
      if (msg.broadcast.user_id === ownmmid) {
        (context.commit as any)(
          'event/avroom/setChannelStatus',
          { channelId: msg.data.channel_id, status: 'left' },
          { root: true }
        );
      }
    },
    getChannelStats(context, channel_id) {
      context
        .dispatch('makeMattermostCall', {
          url: `/api/v4/channels/${channel_id}/stats`,
          method: 'GET'
        })
        .then(stats => {
          context.commit('setChannelStats', {
            id: channel_id,
            count: stats.member_count
          });
        })
        .catch(err => {
          loggerService.warn('Failed to get channel stats', err);
        });
    },
    refreshDgChannel(context, channel_id) {
      // if (cardIsVisible(channel_id)) {
      context.commit('setDiscussionGroupStatus', {
        id: channel_id,
        status: 'loading'
      });
      context
        .dispatch('makeMattermostCall', {
          url: `/api/v4/channels/${channel_id}/members?per_page=5000`,
          method: 'GET'
        })
        .then((users: any[]) => {
          const mmids = users.map(user => user.user_id).filter(it => it !== 'undefined' && it !== 'fakebotid');
          context.commit('setChannel', { id: channel_id, channel: mmids });
          context.commit('setDiscussionGroupStatus', {
            id: channel_id,
            status: 'loaded'
          });
          loggerService.info('Starting user status fetch due to dgChannel refresh');
          return context.dispatch('getUserStatuses', mmids).catch(err => {
            loggerService.error("Failed to get user statuses in refresh! Some cards won't work!", err);
          });
        })
        .catch(err => {
          context.commit('setDiscussionGroupStatus', {
            id: channel_id,
            status: 'error'
          });
          throw err;
        });
      // }
    },
    async leaveAllChannels(context) {
      const allbroadcast = context.rootState.event.event.agenda.broadcasts;

      const nonDiscussionGroupMMID = allbroadcast.reduce(
        (
          acc: any,
          current: {
            mmid: string;
            poll_channel_mmid: string;
            text_qna_channel_mmid: string;
            speaker_chat_mmid: string;
            live_captions_mmid: string;
          }
        ) => {
          return [
            ...acc,
            current.mmid,
            current.poll_channel_mmid,
            current.text_qna_channel_mmid,
            current.speaker_chat_mmid,
            current.live_captions_mmid
          ];
        },
        []
      );

      nonDiscussionGroupMMID.push(context.rootState.event.event.mmid);

      const myId = context.rootState.user.currentUser?.mmid || '';
      const eventID = context.rootState.route.params.eventID || context.rootState.event.event?.id;
      const nonDgChannels = new Set(nonDiscussionGroupMMID);
      let dgChannelMaps = new Set();

      try {
        if (
          !context.rootState.event.useEventApiConsolidation &&
          context.rootState.event.discussionGroups?.length !== 0
        ) {
          let res: any = null;
          try {
            const response = await retrieveDiscussionGroupsV2({ id: eventID?.toString() });
            res = { data: response };
          } catch (err) {
            res = await loadDiscussionGroups({ event: eventID });
          }
          dgChannelMaps = new Set(res.data.map((it: any) => it.mmid));
        }

        const channelsToLeave: string[] = [];
        Object.entries(context.state.dgChannels).forEach(([channelkey, users]) => {
          if ((dgChannelMaps.has(channelkey) || !nonDgChannels.has(channelkey)) && users.indexOf(myId) >= 0) {
            channelsToLeave.push(channelkey);
          }
        });

        const leavePromises = channelsToLeave.map((chid: string) => {
          return context.dispatch('event/avroom/leaveChatChannel', chid, {
            root: true
          });
        });

        return Promise.all(leavePromises).catch(err => {
          loggerService.error('Failure in leaving channel! Some discussionroom cards may not work properly.', err);
        });
      } catch (err) {
        loggerService.warn('Failed to fetch discussionGroups for leaving', err);
      }
    },
    setPublicChannels(context, event) {
      if (context.rootGetters['sidebar/isOverNinetyDays'] && context.rootState.event.useHideSidebar) {
        loggerService.info('on demand mode and over 90 days so not fetching public channels');
        return Promise.resolve([]);
      }
      // town-square is not resolved here when MM_API_CONSOLIDATION is on, so it must not gate the cache hit
      const hasRequiredPublicChannelIds = context.rootState.event.useMmApiConsolidation
        ? Boolean(context.rootState.mattermost.publicChannels.offtopic)
        : Boolean(
            context.rootState.mattermost.publicChannels.offtopic && context.rootState.mattermost.publicChannels.townhall
          );
      if (hasRequiredPublicChannelIds) {
        return Promise.resolve();
      }
      if (publicChannelsInFlight) {
        return publicChannelsInFlight;
      }
      // getCurrentUserDMs already resolves town-square from the joined-channels list, so skip the name lookup
      const townSquare_p = context.rootState.event.useMmApiConsolidation
        ? Promise.resolve(null)
        : context.dispatch('makeMattermostCall', {
            url: `/api/v4/teams/${event.mmid}/channels/name/town-square`,
            method: 'GET'
          });
      const offTopic_p = context.dispatch('makeMattermostCall', {
        url: `/api/v4/teams/${event.mmid}/channels/name/off-topic`,
        method: 'GET'
      });
      publicChannelsInFlight = Promise.all([offTopic_p, townSquare_p])
        .then(([offTopicRes, townSquareRes]) => {
          context.commit('setPublicChannelIds', {
            offtopic: (offTopicRes && offTopicRes.id) || '',
            townhall: (townSquareRes && townSquareRes.id) || ''
          });
        })
        .then(() => ({ event }))
        .finally(() => {
          publicChannelsInFlight = null;
        });
      return publicChannelsInFlight;
    },
    getUserStatuses(context, userids: string[]) {
      const user_ids = userids.filter(id => !!id && id !== 'null' && id !== 'fakebotid' && id !== 'undefined');
      if (user_ids.length === 0) {
        return;
      }
      context
        .dispatch('makeMattermostCall', {
          url: `/api/v4/users/status/ids`,
          method: 'POST',
          body: user_ids
        })
        .then(res => {
          if (res && res.length) {
            const statuses = res.reduce((statuses: any, user: any) => {
              statuses[user.user_id] = user.status;
              return statuses;
            }, {});
            context.commit('setUserStatuses', statuses);
          } else {
            loggerService.error('Expected error got: ', {
              hasResponse: Boolean(res),
              responseType: Array.isArray(res) ? 'array' : typeof res
            });
          }
        });
    },
    getJoinedChannels(context) {
      const currentUser = (context.rootState as any).user.currentUser;
      const currentEvent = context.rootGetters['event/currentEvent'];
      context.state.joinedChannels.state = 'loading';
      return context
        .dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `/api/v4/users/${currentUser?.mmid}/teams/${currentEvent?.mmid}/channels`,
            method: 'GET'
          },
          { root: true }
        )
        .then(res => {
          context.state.joinedChannels.state = 'loaded';
          context.state.joinedChannels.value = res.reduce((all: Record<string, any>, ch: any) => {
            all[ch.id] = Object.freeze({
              name: ch.name,
              id: ch.id,
              type: ch.type
            });
            return all;
          }, {});
          const earlyJoinOptimizationIsSet = context.rootState.event?.event?.channel_sync_triggered ?? false;
          if (earlyJoinOptimizationIsSet) {
            const randomEventJitter = (context.rootState.event.avroom?.jitterdelay || 15) * Math.random();
            setTimeout(() => {
              context.dispatch('event/avroom/joinAllBroadcastsSequentially', 0, {
                root: true
              });
            }, randomEventJitter * 1000);
          }
        })
        .catch(async err => {
          const statusCode = err?.statusCode ?? err?.status ?? err?.response?.status;
          const teamId = currentEvent?.mmid;
          // 404 here means the user has no channels in this team, which also happens when they aren't a team member yet
          if ((statusCode === 403 || statusCode === 404) && teamId && !teamsRetriedAfterAssignment.has(teamId)) {
            teamsRetriedAfterAssignment.add(teamId);
            const assignmentResult = await assignMattermostUserToTeam({
              userId: currentUser?.id,
              teamId,
              eventId: currentEvent?.id
            });

            if (assignmentResult.status === 'success') {
              return context.dispatch('getJoinedChannels').finally(() => teamsRetriedAfterAssignment.delete(teamId));
            }

            teamsRetriedAfterAssignment.delete(teamId);
            if (assignmentResult.status === 'blocked') {
              loggerService.warn(
                'Mattermost user assignment is blocked; not retrying joined channels',
                assignmentResult
              );
            } else {
              loggerService.error('Failed to create mattermost user and assign team user', assignmentResult.error);
            }
          }

          context.state.joinedChannels.state = 'error';
          context.state.joinedChannels.value = {};
        });
    },
    getDiscussionGroupWithoutLoader(context, event) {
      const boothDiscussionGroupIds = (context.rootState.event.event?.booths ?? []).flatMap((booth: any) => {
        return (booth?.discussion_group_objects ?? []).map((it: any) => it?.mmid);
      });
      let discussionGroupIds = (context.rootState.event.discussionGroups || []).map(it => it.mmid);
      discussionGroupIds = discussionGroupIds.concat(boothDiscussionGroupIds);
      const routeDg = context.rootState.event.routeDiscussionGroup;
      if (routeDg) {
        discussionGroupIds.push(routeDg.mmid);
      }

      if (context.rootState.mattermost.discussionGroupLoadingInBackground) {
        return context.rootState.mattermost.discussionGroupLoadingInBackground;
      }
      if (discussionGroupIds.length > 0) {
        context.state.discussionGroupLoadingInBackground = context
          .dispatch('makeMattermostCall', {
            url: `/api/v4/teams/${event.mmid}/channels/members`,
            method: 'POST',
            body: JSON.stringify(discussionGroupIds)
          })
          .then((res: { [k: string]: any[] }) => {
            loggerService.info('Processing channel members');
            const allmmids: string[] = [];
            const dgUsersByChannel = Object.keys(res || {}).reduce((accum: { [k: string]: any }, channelkey) => {
              const channel_users = res[channelkey];
              accum[channelkey] = channel_users.map(user => {
                allmmids.push(user.user_id);
                return user.user_id;
              });
              return accum;
            }, {});
            return { allmmids, dgUsersByChannel };
          })
          .then(({ allmmids, dgUsersByChannel }) => {
            loggerService.info('Starting user status fetch');
            return context
              .dispatch('getUserStatuses', allmmids)
              .then(() => {
                return { dgUsersByChannel };
              })
              .catch(err => {
                loggerService.error("Failed to get user statuses! Some cards won't work!", err);
                return { dgUsersByChannel };
              });
          })
          .then(({ dgUsersByChannel }) => {
            loggerService.info('Finally setting card members');
            const dgChannels = {} as PropType<MattermostState, 'dgChannels'>;
            Object.keys(dgUsersByChannel).forEach(channelkey => {
              const mmids = dgUsersByChannel[channelkey];
              dgChannels[channelkey] = mmids;
              context.commit('setDiscussionGroupStatus', {
                id: channelkey,
                status: 'loaded'
              });
            });
            context.commit('setDgChannels', dgChannels);
            context.state.discussionGroupLoadingInBackground = null;
          })
          .catch(err => {
            loggerService.error(
              'Failed to get discussion group members! You may not see correct people on the discussion group cards.',
              err
            );
            context.state.discussionGroupLoadingInBackground = null;
            return {};
          });
      } else {
        loggerService.info('No discussion groups were found so not loading any data for them');
        context.state.discussionGroupLoadingInBackground = null;
      }
      return context.state.discussionGroupLoadingInBackground;
    }
  }
};

export default mattermostmodule;

function createMMInstance() {
  const mmTeamId = store.state.event.event?.mmid;
  const socketUrl = `${getEnv('VUE_APP_SOCKET_MATTERMOST')}?team_id=${mmTeamId}`;
  socket = new MMClient(socketUrl, loggerService);
}

function makeRefreshFunction(context: ActionContext<MattermostState, RootState>, channel_id: string) {
  let debouncedRefresh = Function.prototype;
  if (channel_id) {
    debouncedRefresh = Function.prototype;
    // loggerService.error("No debounced function for " + channel_id);
    if (groupIsInCurrentDisplayedList(context, channel_id)) {
      debouncedRefresh = getDebouncedRefreshFunction(channel_id, context, 2000);
    } else if (groupIsBroadcast(context, channel_id)) {
      debouncedRefresh = getDebouncedRefreshFunction(channel_id, context, 10 * 1000);
    }
  }
  return debouncedRefresh;
}

function groupIsInCurrentDisplayedList(context: ActionContext<MattermostState, RootState>, channel_id: string) {
  const inRoom = context.rootState.event.discussionGroups.find((group: any) => group.mmid === channel_id);

  const booth = context.rootGetters['event/currentEventBooth'];
  const inBoothRooms = (((booth && booth.discussion_group_objects) || []) as any[]).find(
    (group: any) => group?.mmid === channel_id
  );

  return !!inRoom || !!inBoothRooms;
}

export function groupIsBroadcast(context: ActionContext<MattermostState, RootState>, channel_id: string) {
  const currentBroad_id = context.rootState.route.params.id;
  const currentBroadcast = context.rootState.event?.event?.agenda?.broadcast?.find(
    (b: any) => b.id === currentBroad_id
  );
  let mmids: string[] = [];
  if (currentBroadcast) {
    mmids = [currentBroadcast.mmid, currentBroadcast.poll_channel_mmid, currentBroadcast.text_qna_channel_mmid];
  }
  return mmids.includes(channel_id);
}

function getDebouncedRefreshFunction(
  channel_id: string,
  context: ActionContext<MattermostState, RootState>,
  interval: number
) {
  let debouncedRefresh = channelRefreshDebouncedFunctions[channel_id];
  if (!debouncedRefresh) {
    channelRefreshDebouncedFunctions[channel_id] = debounce(
      () => {
        context.dispatch('refreshDgChannel', channel_id);
      },
      interval,
      {
        leading: true,
        maxWait: 10 * 1000
      }
    );
    debouncedRefresh = channelRefreshDebouncedFunctions[channel_id];
  }
  return debouncedRefresh;
}
function isSpeaker(context: any) {
  const broadcast = context.rootGetters['event/currentBroadcast'];
  const speakerIDs = broadcast?.speakerIds || [];
  const uid = context.rootState?.user?.currentUser?.id;
  const tvMode = context.rootState.event.stage?.isTVMode;
  return speakerIDs.includes(uid) && tvMode;
}

export function isOrganizer(context: any) {
  const tvMode = context.rootState.event.stage?.isTVMode;
  return context.rootGetters['event/isBroadcastOrganizer'] && !tvMode;
}

function isLiteModeActive(context: any) {
  const broadcast = context.rootGetters['event/currentBroadcast'];
  const isLiteModeActive =
    context.rootState.user?.liteMode?.liteModeActive &&
    broadcast.broadcast_type !== 'SIMULIVE' &&
    broadcast.broadcast_type !== 'LIVE_EXTERNAL';
  const isAuthorizedToBackstage = isSpeaker(context) || isOrganizer(context);
  return isLiteModeActive && !isAuthorizedToBackstage;
}
